import { React } from 'imports';
import Enum from 'utils/Enum';
import './Diagram.less';

const LICENSE_KEY = '298647e0bb654ec702d90676423d6bbc5cf07e34cf960ef3590346f5ee586840219bec7906dadec580f14afc1a7bc1d98ec26c3b874b576be231d58f45b2d3fbb7367bbb140d4487ac0772c5ccf82fa3a97b25a296b027f5d87bdbf4eca0c4c90cedf3801fce59ba7d7d0e234678fa1ab1fb8b2bf810dc4c6a6e9db8fae9b04bfa';

// a placeholder for GoJS based diagram component
// currently it's just checks and exposes GoJS DLL

// we can not use 'import' here since webpack tries to evaluate all imported modules as soon as current module is imported somewhere
// highcharts are loaded as DLL dynamically
// 'require'd modules however are evaluated only when 'require' is call, so it allows us to evaluate highcharts modules only when some component actually renders Chart
// usually it means all DLL dependencies are ensured

let GoJS = null;

function loadGoJS() {
    if (typeof DLL_PATHS !== 'undefined' && !window.gojs) {
        throw new Error('gojs DLL has to be loaded before using Diagram.');
    }

    if (DEBUG) {
        GoJS = require('gojs/release/go-debug-module.js');
    }
    else {
        GoJS = require('gojs/release/go-module.js');
    }

    GoJS.licenseKey = LICENSE_KEY;
    GoJS.Diagram.licenseKey = LICENSE_KEY;
    return GoJS;
}

export const TransactionType = Enum('UPDATE_MODEL', 'CREATE_LAYOUT_PARTS');

/*
    This is an extended version of the GoJS's default text editor. https://gojs.net/latest/extensions/TextEditor.js
    Currently the only difference is that the text editor resizes itself to match with the parent TextBlock's height.
*/
/* eslint-disable func-names */
function extendedExtendedTextEditor(go) {
    const
        ExtendedTextEditor = new go.HTMLInfo(),
        textarea = document.createElement('textarea');

    textarea.id = 'myTextArea';

    textarea.addEventListener('input', function(_event) {
        const { tool } = ExtendedTextEditor;
        if (tool.textBlock === null) { return; }

        const
            tempText = tool.measureTemporaryTextBlock(this.value),
            scale = this.textScale;

        this.style.width = `${20 + tempText.measuredBounds.width * scale}px`;
        this.rows = tempText.lineCount;
    }, false);

    textarea.addEventListener('keydown', (event) => {
        if (event.isComposing) { return; }
        const { tool } = ExtendedTextEditor;

        if (tool.textBlock === null) { return; }
        const { key } = event;

        if (key === 'Enter') {
            if (tool.textBlock.isMultiline === false) { event.preventDefault(); }
            tool.acceptText(go.TextEditingTool.Enter);

        }
        else if (key === 'Tab') {
            tool.acceptText(go.TextEditingTool.Tab);
            event.preventDefault();

        }
        else if (key === 'Escape') {
            tool.doCancel();
            if (tool.diagram !== null) { tool.diagram.doFocus(); }
        }
    }, false);

    // handle focus:
    textarea.addEventListener('focus', (_event) => {
        const { tool } = ExtendedTextEditor;
        if (!tool || tool.currentExtendedTextEditor === null || tool.state === go.TextEditingTool.StateNone) { return; }

        if (tool.state === go.TextEditingTool.StateActive) {
            tool.state = go.TextEditingTool.StateEditing;
        }

        if (tool.selectsTextOnActivate) {
            textarea.select();
            textarea.setSelectionRange(0, 9999);
        }
    }, false);

    // Disallow blur.
    // If the textEditingTool blurs and the text is not valid,
    // we do not want focus taken off the element just because a user clicked elsewhere.
    textarea.addEventListener('blur', (_event) => {
        const { tool } = ExtendedTextEditor;
        if (!tool || tool.currentExtendedTextEditor === null || tool.state === go.TextEditingTool.StateNone) { return; }

        textarea.focus();

        if (tool.selectsTextOnActivate) {
            textarea.select();
            textarea.setSelectionRange(0, 9999);
        }
    }, false);

    ExtendedTextEditor.valueFunction = () => textarea.value;
    ExtendedTextEditor.mainElement = textarea; // to reference it more easily
    ExtendedTextEditor.tool = null; // Initialize

    // used to be in doActivate
    ExtendedTextEditor.show = function(textBlock, diagram, tool) {
        if (!(textBlock instanceof go.TextBlock)) { return; }
        if (ExtendedTextEditor.tool !== null) { return; } // Only one at a time.

        ExtendedTextEditor.tool = tool; // remember the TextEditingTool for use by listeners

        // This is called during validation, if validation failed:
        if (tool.state === go.TextEditingTool.StateInvalid) {
            textarea.style.border = '3px solid red';
            textarea.focus();
            return;
        }

        // This part is called during initalization:

        const
            loc = textBlock.getDocumentPoint(go.Spot.Center),
            pos = diagram.position,
            sc = diagram.scale;

        let textScale = textBlock.getDocumentScale() * sc;
        if (textScale < tool.minimumEditorScale) { textScale = tool.minimumEditorScale; }

        // Add slightly more width/height to stop scrollbars and line wrapping on some browsers
        // +6 is firefox minimum, otherwise lines will be wrapped improperly
        const
            textWidth = textBlock.naturalBounds.width * textScale + 6,
            textHeight = textBlock.naturalBounds.height * textScale + 2,
            left = (loc.x - pos.x) * sc,
            yCenter = (loc.y - pos.y) * sc, // this is actually the center, used to set style.top
            verticalAlign = textBlock.verticalAlignment,
            oneLineHeight = textBlock.lineHeight + textBlock.spacingAbove + textBlock.spacingBelow,
            allLinesHeight = oneLineHeight * textBlock.lineCount * textScale,
            center = 0.5 * textHeight - 0.5 * allLinesHeight,
            // add offset to yCenter to get the appropriate position:
            yOffset = verticalAlign.y * textHeight - verticalAlign.y * allLinesHeight + verticalAlign.offsetY - center - allLinesHeight / 2;

        textarea.value = textBlock.text;
        // the only way you can mix font and fontSize is if the font inherits and the fontSize overrides
        // in the future maybe have textarea contained in its own div
        diagram.div.style.font = textBlock.font;

        const paddingSize = 1;
        textarea.style.position = 'absolute';
        textarea.style.zIndex = '100';
        textarea.style.font = 'inherit';
        textarea.style.fontSize = `${textScale * 100}%`;
        textarea.style.lineHeight = 'normal';
        textarea.style.width = `${textWidth}px`;
        textarea.style.left = `${(left - textWidth / 2 | 0) - paddingSize}px`;
        textarea.style.top = `${(yCenter + yOffset | 0) - paddingSize}px`;
        textarea.style.textAlign = textBlock.textAlign;
        textarea.style.margin = '0';
        textarea.style.padding = `${paddingSize}px`;
        textarea.style.border = '0';
        textarea.style.outline = 'none';
        textarea.style.whiteSpace = 'pre-wrap';
        textarea.style.overflow = 'hidden'; // for proper IE wrap
        textarea.rows = textBlock.lineCount;
        textarea.textScale = textScale; // attach a value to the textarea, for convenience
        textarea.className = 'goTXarea';

        if (textHeight > 0) { textarea.style.height = `${textHeight}px`; }

        // Show:
        diagram.div.appendChild(textarea);

        // After adding, focus:
        textarea.focus();
        if (tool.selectsTextOnActivate) {
            textarea.select();
            textarea.setSelectionRange(0, 9999);
        }
    };

    ExtendedTextEditor.hide = (diagram, _tool) => {
        diagram.div.removeChild(textarea);
        ExtendedTextEditor.tool = null; // forget reference to TextEditingTool
    };

    return ExtendedTextEditor;
    /* eslint-enable func-names */
}

export default class Diagram extends React.PureComponent {
    static get GoJS() {
        return GoJS = GoJS || loadGoJS();
    }

    static get TransactionType() {
        return TransactionType;
    }

    static get ExtendedTextEditor() {
        return extendedExtendedTextEditor(this.GoJS);
    }

    render() {
        // TODO implement some static config and reconciliation stuff similar to Chart component
        throw new Error('Not implemented.');
    }
}

